import React, { useState, useEffect } from 'react';
import { WifiOff, RefreshCw, CheckCircle2, CloudUpload } from 'lucide-react';
import { onQueueChange, flushQueue } from '../lib/offlineSync';
import api from '../lib/api';

/**
 * OfflineBanner — badge flottant en bas d'écran.
 *  - Rouge : hors ligne, avec compteur si mutations en file
 *  - Ambre : en ligne mais file non vide (synchro en cours ou manuelle)
 *  - Vert (fugace) : synchro terminée
 *  - Caché quand tout est OK
 */
export function OfflineBanner() {
  const [online, setOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [queued, setQueued] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [flashOk, setFlashOk] = useState(false);

  useEffect(() => {
    const onOn = () => setOnline(true);
    const onOff = () => setOnline(false);
    window.addEventListener('online', onOn);
    window.addEventListener('offline', onOff);
    const off = onQueueChange((n) => setQueued(n));
    return () => {
      window.removeEventListener('online', onOn);
      window.removeEventListener('offline', onOff);
      off?.();
    };
  }, []);

  const syncNow = async () => {
    setSyncing(true);
    try {
      const res = await flushQueue(api);
      if (res.ok > 0) {
        setFlashOk(true);
        setTimeout(() => setFlashOk(false), 2500);
      }
    } finally {
      setSyncing(false);
    }
  };

  if (online && queued === 0 && !flashOk) return null;

  let style = 'bg-red-600 text-white';
  let icon = <WifiOff className="w-4 h-4" />;
  let text = 'Hors ligne';

  if (online && queued === 0 && flashOk) {
    style = 'bg-emerald-600 text-white';
    icon = <CheckCircle2 className="w-4 h-4" />;
    text = 'Synchronisation terminée';
  } else if (online && queued > 0) {
    style = 'bg-amber-500 text-white';
    icon = syncing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CloudUpload className="w-4 h-4" />;
    text = `${queued} modification(s) à synchroniser`;
  } else if (!online && queued > 0) {
    text = `Hors ligne — ${queued} modification(s) en attente`;
  }

  return (
    <div
      className={`fixed bottom-4 left-1/2 -translate-x-1/2 z-50 rounded-full shadow-lg px-4 py-2 text-xs font-semibold flex items-center gap-2 ${style}`}
      data-testid="offline-banner"
    >
      {icon}
      <span>{text}</span>
      {online && queued > 0 && (
        <button
          onClick={syncNow}
          disabled={syncing}
          className="ml-1 bg-white/20 hover:bg-white/30 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide"
          data-testid="offline-sync-now-btn"
        >
          {syncing ? 'Synchro…' : 'Synchroniser'}
        </button>
      )}
    </div>
  );
}

export default OfflineBanner;
